import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import Reveal from "@/components/Reveal";

const Gallery = ({ content }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  if (!content || !content.images || content.images.length === 0) return null;

  return (
    <section id="galeria" className="py-24 md:py-32 bg-transparent text-sm-text border-t border-sm-border">
      <div className="sm-container">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          {content.eyebrow && (
            <Reveal>
              <p className="sm-eyebrow text-sm-olive">{content.eyebrow}</p>
            </Reveal>
          )}
          {content.title && (
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-6 italic font-light">
                {content.title}
              </h2>
            </Reveal>
          )}
        </div>

        <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
          {content.images.map((img, i) => (
            <Reveal key={i} delay={(i % 3) * 0.1} className="break-inside-avoid block">
              <div 
                className="relative group overflow-hidden cursor-pointer w-full h-full" 
                onClick={() => setSelectedImage(img)}
              >
                <img
                  src={img.url}
                  alt={img.alt || `Gallery image ${i + 1}`}
                  className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-500 flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 text-white font-serif italic text-lg transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                    Zmadho
                  </span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Lightbox Overlay */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4 md:p-12 cursor-pointer"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(null);
              }}
            >
              <X className="w-8 h-8" />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              src={selectedImage.url}
              alt={selectedImage.alt || "Fullscreen image"}
              className="max-w-full max-h-full object-contain cursor-default"
              onClick={(e) => e.stopPropagation()}
            />
            {selectedImage.alt && (
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/80 font-serif italic text-sm md:text-base text-center"
              >
                {selectedImage.alt}
              </motion.p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
